import React, { useEffect } from "react";
import "./Projectra.css";

import ProjectraDator1 from "../../assets/projectra9.png";
import ProjectraDator2 from "../../assets/projectra8.png";

import ProjectraHero from "../../assets/projectra1.png";

import ProjectraMockup1 from "../../assets/projectra10.jpg";
import ProjectraMockup2 from "../../assets/projectra2.png";
import ProjectraMockup3 from "../../assets/projectra3.png";

import ProjectraMockup4 from "../../assets/projectra4.jpg";
import ProjectraMockup5 from "../../assets/projectra6.jpg";

function useProjectraScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll(".projectra-reveal");
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

export default function Projectra() {
  useProjectraScrollReveal();

  return (
    <main className="projectra-page">
      <div className="projectra-bg-shape"></div>

      {/* ================= HERO ================= */}
      <section className="projectra-hero projectra-reveal">
        <div className="projectra-hero-copy">
          <p className="projectra-hero-kicker">
            Design and Development Project · PROJECTRA
          </p>

          <h1 className="projectra-hero-title">
            PROJECTRA
            <span className="projectra-hero-title-accent">
              Designing a digital construction logbook
            </span>
          </h1>

          <p className="projectra-hero-body">
            Projectra is a digital construction logbook designed to support multiple
            user roles within the construction industry. The project explores how
            iterative usability evaluation — combining heuristic evaluation and
            usability testing — can improve clarity, navigation, and real-world
            workflow support.
            <br /><br />
            The system was developed and evaluated across low-, mid-, and
            high-fidelity prototypes, with continuous input from construction
            workers, UX experts, and domain specialists.
            <br /><br />
            This case highlights the design decisions, visual direction and
            deliverables created throughout the process.
          </p>

          <div className="projectra-hero-tags">
            <span>UX Research</span>
            <span>Usability Testing</span>
            <span>Interaction Design</span>
          </div>

          <button
            className="projectra-primary-cta"
            type="button"
            onClick={() => window.open("https://projectra.se", "_blank")}
          >
            View the project
          </button>
        </div>

        <div className="projectra-hero-image">
          <div className="projectra-hero-mockup-frame">
            <img src={ProjectraHero} alt="Projectra hero mockup" />
          </div>
        </div>
      </section>

      {/* ================= DESIGN SOLUTION ================= */}
      <section className="projectra-section projectra-reveal">
        <h2>Design solution</h2>
        <p className="projectra-large-text">
          The design solution for Projectra focuses on clarity, role-based
          navigation and usability in real construction workflows. The interface
          is structured around clearly defined tasks, consistent interaction
          patterns and a modular layout system that supports both on-site and
          administrative users.
          <br /><br />
          Design decisions were continuously informed by heuristic evaluation and
          usability testing, allowing usability issues to be identified and
          resolved across multiple iterations and fidelity levels.
        </p>
      </section>

      {/* ================= JOURNEY ================= */}
      <section className="projectra-section projectra-reveal">
        <h2>The Journey</h2>

        <div className="projectra-columns-3">
          <div className="projectra-journey-card">
            <h3>Research</h3>
            <p>
              The project began with an analysis of existing digital construction
              logbooks, combined with a review of relevant usability principles.
              Multiple user roles within the construction industry were identified
              to understand differing needs, priorities and work contexts.
            </p>
          </div>

          <div className="projectra-journey-card">
            <h3>Ideation</h3>
            <p>
              Based on the research findings, several design concepts were explored
              through low- and mid-fidelity prototypes. Interaction patterns,
              navigation structures and task flows were iteratively refined, with
              design decisions continuously informed by usability feedback.
            </p>
          </div>

          <div className="projectra-journey-card">
            <h3>Final Delivery</h3>
            <p>
              The final design is a high-fidelity prototype refined through
              multiple rounds of heuristic evaluation and usability testing. The
              resulting interface supports real-world construction workflows
              while maintaining clarity, consistency and scalability.
            </p>
          </div>
        </div>
      </section>

      {/* ================= DESKTOP MOCKUPS ================= */}
      <section className="projectra-image-row projectra-reveal">
        <img src={ProjectraDator1} alt="Projectra desktop view 1" />
        <img src={ProjectraDator2} alt="Projectra desktop view 2" />
      </section>

      {/* ================= VISUALS ================= */}
      <section className="projectra-section projectra-reveal">
        <h2>Visuals</h2>
        <p className="projectra-large-text">
        The visual design of Projectra is intentionally restrained and functional,
        prioritising clarity and readability in complex construction environments.
        A neutral colour palette, clear typographic hierarchy and consistent spacing
        are used to reduce cognitive load and support quick information scanning
        across different user roles.
        </p>
      </section>

      <section className="projectra-image-row-3 projectra-reveal">
        <img src={ProjectraMockup1} alt="Projectra visual 1" />
        <img src={ProjectraMockup2} alt="Projectra visual 2" />
        <img src={ProjectraMockup3} alt="Projectra visual 3" />
      </section>

      <section className="projectra-image-row-3 projectra-reveal">
        <img src={ProjectraMockup4} alt="Projectra visual 1" />
        <img src={ProjectraMockup5} alt="Projectra visual 2" />
      </section>


    </main>
  );
}
